'use client';

import type { ElementType } from 'react';
import { useEffect, useRef, useState, createElement, useMemo, useCallback } from 'react';

import { gsap } from 'gsap';
import './TextType.css';

type TypingAction =
  | { type: 'delete'; delay: number }
  | { type: 'type'; delay: number; nextChar: string }
  | { type: 'startDeleting'; delay: number }
  | { type: 'advance'; nextTextIndex: number; shouldStop: boolean }
  | { type: 'complete' };

interface TextTypeProps {
  className?: string;
  showCursor?: boolean;
  hideCursorWhileTyping?: boolean;
  cursorCharacter?: string | React.ReactNode;
  cursorBlinkDuration?: number;
  cursorClassName?: string;
  text: string | string[];
  as?: ElementType;
  typingSpeed?: number;
  initialDelay?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
  loop?: boolean;
  textColors?: string[];
  variableSpeed?: { min: number; max: number };
  onSentenceComplete?: (sentence: string, index: number) => void;
  onComplete?: () => void;
  startOnVisible?: boolean;
  reverseMode?: boolean;
}

const TextType = ({
  text,
  as: Component = 'div',
  typingSpeed = 40,
  initialDelay = 0,
  pauseDuration = 2000,
  deletingSpeed = 30,
  loop = true,
  className = '',
  showCursor = true,
  hideCursorWhileTyping = false,
  cursorCharacter = '|',
  cursorClassName = '',
  cursorBlinkDuration = 0.5,
  textColors = [],
  variableSpeed,
  onSentenceComplete,
  onComplete,
  startOnVisible = false,
  reverseMode = false,
  ...props
}: TextTypeProps & React.HTMLAttributes<HTMLElement>) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(!startOnVisible);
  const cursorRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  const textArray = useMemo(() => (Array.isArray(text) ? text : [text]), [text]);

  const getRandomSpeed = useCallback(() => {
    if (!variableSpeed) return typingSpeed;
    const { min, max } = variableSpeed;
    const range = max - min;
    if (range <= 0) return min;
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
      return min;
    }
    const values = new Uint32Array(1);
    crypto.getRandomValues(values);
    const normalized = values[0] / (0xffffffff + 1);
    return min + normalized * range;
  }, [variableSpeed, typingSpeed]);

  const handleDeleteTick = useCallback(() => {
    setDisplayedText(prev => prev.slice(0, -1));
  }, []);

  const handleTypeTick = useCallback((nextChar: string) => {
    setDisplayedText(prev => prev + nextChar);
    setCurrentCharIndex(prev => prev + 1);
  }, []);

  const handleStartDeleting = useCallback(() => {
    setIsDeleting(true);
  }, []);

  const getCurrentTextColor = () => {
    if (textColors.length === 0) return;
    return textColors[currentTextIndex % textColors.length];
  };

  useEffect(() => {
    if (!startOnVisible || !containerRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [startOnVisible]);

  useEffect(() => {
    if (showCursor && cursorRef.current) {
      gsap.set(cursorRef.current, { opacity: 1 });
      gsap.to(cursorRef.current, {
        opacity: 0,
        duration: cursorBlinkDuration,
        repeat: -1,
        yoyo: true,
        ease: 'power2.inOut'
      });
    }
  }, [showCursor, cursorBlinkDuration]);

  useEffect(() => {
    if (!isVisible) return;

    let timeout: NodeJS.Timeout;

    const currentText = textArray[currentTextIndex];
    const processedText = reverseMode ? currentText.split('').reverse().join('') : currentText;
    const action = getTypingAction({
      isDeleting,
      displayedText,
      currentCharIndex,
      processedText,
      textArrayLength: textArray.length,
      currentTextIndex,
      loop,
      typingSpeed,
      initialDelay,
      pauseDuration,
      variableSpeed,
      getRandomSpeed,
      deletingSpeed
    });

    if (!action) return;

    switch (action.type) {
      case 'delete':
        timeout = setTimeout(handleDeleteTick, action.delay);
        break;
      case 'type':
        timeout = setTimeout(handleTypeTick, action.delay, action.nextChar);
        break;
      case 'startDeleting':
        timeout = setTimeout(handleStartDeleting, action.delay);
        break;
      case 'advance':
        setIsDeleting(false);
        if (action.shouldStop) return;
        if (onSentenceComplete) {
          onSentenceComplete(textArray[currentTextIndex], currentTextIndex);
        }
        setCurrentTextIndex(action.nextTextIndex);
        setCurrentCharIndex(0);
        break;
      case 'complete':
        if (onComplete) onComplete();
        return;
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [
    currentCharIndex,
    displayedText,
    isDeleting,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
    textArray,
    currentTextIndex,
    loop,
    initialDelay,
    isVisible,
    reverseMode,
    variableSpeed,
    onSentenceComplete,
    handleDeleteTick,
    handleTypeTick,
    handleStartDeleting,
    getRandomSpeed,
    onComplete
  ]);

  const shouldHideCursor =
    hideCursorWhileTyping && (currentCharIndex < textArray[currentTextIndex].length || isDeleting);

  return createElement(
    Component,
    // eslint-disable-next-line react-hooks/refs
    {
      ref: containerRef,
      className: `text-type ${className}`,
      ...props
    },
    <span
      className='text-type__content'
      style={{ color: getCurrentTextColor() || 'inherit' }}
    >
      {displayedText}
    </span>,
    showCursor && (
      <span
        ref={cursorRef}
        className={`text-type__cursor ${cursorClassName} ${shouldHideCursor ? 'text-type__cursor--hidden' : ''}`}
      >
        {cursorCharacter}
      </span>
    )
  );
};

export default TextType;

const getTypingAction = ({
  isDeleting,
  displayedText,
  currentCharIndex,
  processedText,
  textArrayLength,
  currentTextIndex,
  loop,
  typingSpeed,
  initialDelay,
  pauseDuration,
  variableSpeed,
  getRandomSpeed,
  deletingSpeed
}: {
  isDeleting: boolean;
  displayedText: string;
  currentCharIndex: number;
  processedText: string;
  textArrayLength: number;
  currentTextIndex: number;
  loop: boolean;
  typingSpeed: number;
  initialDelay: number;
  pauseDuration: number;
  variableSpeed?: { min: number; max: number };
  getRandomSpeed: () => number;
  deletingSpeed: number;
}): TypingAction | null => {
  if (isDeleting) {
    if (displayedText === '') {
      const isLast = currentTextIndex === textArrayLength - 1;
      return {
        type: 'advance',
        nextTextIndex: (currentTextIndex + 1) % textArrayLength,
        shouldStop: isLast && !loop
      };
    }
    return { type: 'delete', delay: deletingSpeed };
  }

  if (currentCharIndex < processedText.length) {
    const shouldDelayStart = currentCharIndex === 0 && displayedText === '';
    const startDelay = shouldDelayStart ? initialDelay : 0;
    const speed = variableSpeed ? getRandomSpeed() : typingSpeed;
    return {
      type: 'type',
      delay: startDelay + speed,
      nextChar: processedText[currentCharIndex]
    };
  }

  if (textArrayLength >= 1) {
    if (!loop && currentTextIndex === textArrayLength - 1) {
      return { type: 'complete' };
    }
    return { type: 'startDeleting', delay: pauseDuration };
  }

  return null;
};
